import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Flex, notification, Tooltip } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import { fetchUserForSearch } from "../../Services/user_question_history";
import CustomTable from "../../Component/Table";
import { DeleteOutlined } from "@ant-design/icons";
import { FilterContainer } from "../../Styles/filterStyle";
import DebouncedInput from "../../Component/DebouncedInput";
import { updateTagUsers, getTagUser, DelTagMappedUsers} from "../../Services/tags"



const columns = (handleDelClick) => [
    { title: "User ID", dataIndex: "id", key: "id" },
    { title: "User Name", dataIndex: "name", key: "name" },
    { title: "User Email", dataIndex: "email", key: "email" },
    {
        title: "Actions",
        key: "actions",
        fixed: 'right',
        render: (_, record) => (
            <Flex gap="small">
                <Tooltip title="Remove user from this tag">
                    <Button color="danger" variant="link" icon={<DeleteOutlined />} onClick={() => handleDelClick(record)}></Button>
                </Tooltip>
            </Flex>
        ),
    },
];

const TagMapUserModal = ({
    TagMapVisible,
    masterTag,
    onClose
}) => {
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const skip = (currentPage - 1) * pageSize;
    const [searchTerm, setSearchTerm] = useState("");

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ["mass_users", currentPage, pageSize, masterTag?.id, searchTerm],
        queryFn: () =>
        getTagUser({
            tag_id: masterTag?.id,
            skip: skip,
            limit: pageSize,
            name: searchTerm
        }),
        enabled: !!masterTag?.id && TagMapVisible,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const resetForm = () => {
        form.resetFields();
        setSelectedUserIds([]);
    };

    useEffect(() => {
        if (TagMapVisible) {
            setCurrentPage(1);
        }
    }, [form, TagMapVisible, searchTerm]);

    const onFinish = async (values) => {
        const filtered_user_id = [...selectedUserIds].filter(a => Number.isInteger(a));
                    
        if (filtered_user_id.length === 0) {
            return notification.error({
                message: "Please select at least one valid email",
            });
        }
        tagMutation.mutate({ id: masterTag.id, user_ids: filtered_user_id });
    };

    const handleDelClick = (record) => {
        // use mudation to call
        DelUserTagMutation.mutate({ user_id: record.id, tag_id: masterTag.id });
    };

    const DelUserTagMutation = useMutation({
        mutationFn: (data) => DelTagMappedUsers(data.user_id, data.tag_id),
        onSuccess: (res) => {
            if (res) {
                // do close the dialog
                resetForm();
                // onClose()
                queryClient.invalidateQueries(["mass_users"]);
            }

        },
    });

    const tagMutation = useMutation({
        mutationFn: (data) => updateTagUsers(data.id, data.user_ids),
        onSuccess: (res) => {
            if (res) {
                // do close the dialog
                resetForm();
                // onClose()
                queryClient.invalidateQueries(["mass_users"]);
            }

        },
    });

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    return (
        <Modal
            title={`#${masterTag?.id ?? ""} Map User to "${masterTag?.name ?? ""}" Tag`}
            open={TagMapVisible}
            width={650}
            footer={null}
            onCancel={() => {
                setSearchTerm("")
                form.resetFields();
                onClose();
            }}
            destroyOnClose
            maskClosable={false}
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item
                    name="users"
                    label="Select Users to Map"
                    rules={[{ required: true, message: "User is Required", whitespace: true }]}
                >
                    <CustomFilterSelect
                        fetchOptions={fetchUserForSearch}
                        label="Users"
                        filterKey="user"
                        onChange={(key, value) => {
                            form.setFieldValue({ users: value });
                            setSelectedUserIds(value);
                        }}
                        renderOption={(option) =>
                            `#${option.value} ${option.label} (${option.email})`
                        }
                        className="custom-user-select"
                        mode="multiple"
                        value={selectedUserIds}
                        copyPaste={true}
                    />
                </Form.Item>

                <Form.Item>
                    <div className="flex items-center justify-end gap-3">
                        {/* <Button onClick={onClose}>Cancel</Button> */}
                        <Button type="primary" htmlType="submit" loading={tagMutation.isPending}>
                            Assign Tag to User
                        </Button>
                    </div>
                </Form.Item>
            </Form>

            <hr style={{"marginBottom":"10px"}}></hr>           
            {/* <h3 style={{"marginBottom":"10px"}}>"{masterTag?.name}" Tag Mapped Users</h3> */}

            <FilterContainer>
                <div style={{ display: "flex", flexDirection: "column", maxWidth: 300}}>
                    <DebouncedInput
                        placeholder="Search by Name, email, id."
                        value={searchTerm}
                        onChange={setSearchTerm}
                        delay={1000}
                        width={300}
                    />
                    <small style={{ color: "#888", marginTop: 4 }}>
                        Search by ID, name, or email (comma-separated).
                    </small>
                </div>
            </FilterContainer>

            <CustomTable
                columns={columns(handleDelClick)}
                data={data?.users || []}
                loading={isLoading || isFetching}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleTableChange}
                maxHeight="calc(100vh - 300px)"
            />
        </Modal>
    );
};

export default TagMapUserModal;
